// src/components/extraction/CameraCaptureButton.jsx
// Öffnet auf Mobilgeräten direkt die Kamera-App (statt Datei-/Galerie-Auswahl) über
// das capture-Attribut. Auf Desktop-Browsern ohne Kamera fällt der Input einfach auf
// den normalen Dateidialog zurück - kein Feature-Detection nötig.
//
// Zusätzlich: Erkennung querformatiger Aufnahmen. Bei manchen Handys mit automatischer
// Ausrichtung dreht sich beim Auslösen spontan ins Querformat - das im Backend erkannte
// EXIF-Tag (meist "180° drehen") kann das nicht reparieren, da die Rohpixel selbst schon
// querformatig sind (eine reine 180°-Drehung ändert nie das Seitenverhältnis). Deshalb hier
// clientseitig prüfen, per createImageBitmap(file, { imageOrientation: 'from-image' }): Die
// Spezifikation garantiert, dass das zurückgegebene Bitmap bereits vollständig EXIF-rotiert
// ist, sobald das Promise auflöst. Die Rückmeldung an den Nutzer läuft über den globalen
// Dialog (useDialog) statt eines eigenen, inline gerenderten Warnkastens - erscheint dadurch
// sofort als Overlay, ohne dass man erst herunterscrollen muss.
import React, { useRef } from 'react'
import { useDialog } from './Dialog'

async function pruefeAusrichtung (file) {
  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: 'from-image'
    })
    const istQuerformat = bitmap.width > bitmap.height
    bitmap.close()
    return {
      istQuerformat,
      previewUrl: istQuerformat ? URL.createObjectURL(file) : null
    }
  } catch {
    // createImageBitmap nicht unterstützt oder Format nicht decodierbar (z.B. sehr alter
    // Browser) - Prüfung einfach überspringen, statt den Upload zu blockieren.
    return { istQuerformat: false, previewUrl: null }
  }
}

function CameraCaptureButton ({
  onFileSelected,
  disabled,
  label = '📷 Aufnehmen',
  className = ''
}) {
  const inputRef = useRef(null)
  const { confirm } = useDialog()

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleChange = async e => {
    const selected = e.target.files?.[0]
    e.target.value = '' // erlaubt erneutes Aufnehmen direkt danach
    if (!selected) return

    const { istQuerformat, previewUrl } = await pruefeAusrichtung(selected)

    if (!istQuerformat) {
      onFileSelected(selected)
      return
    }

    const trotzdemVerwenden = await confirm({
      titel: 'Querformat erkannt',
      nachricht: (
        <>
          <p>
            Dieses Foto scheint im Querformat aufgenommen worden zu sein -
            Mobilgeräte drehen beim Auslösen manchmal spontan: Erkennung bei
            Dokumenten im Hochformat deutlich schlechter.
          </p>
          {previewUrl && (
            <img
              src={previewUrl}
              alt='Vorschau der Aufnahme'
              className='querformat-warnung-bild'
            />
          )}
        </>
      ),
      bestaetigenLabel: 'Trotzdem verwenden',
      abbrechenLabel: 'Verwerfen & neu aufnehmen',
      abbrechenBetont: true // "Verwerfen & neu aufnehmen" bleibt die empfohlene, hervorgehobene Aktion
    })

    if (previewUrl) URL.revokeObjectURL(previewUrl)

    if (trotzdemVerwenden) {
      onFileSelected(selected)
    }
    // sonst: verworfen - der Nutzer nimmt ein neues Foto auf, hier ist nichts weiter zu tun
  }

  return (
    <>
      <button
        type='button'
        className={`btn ${className}`.trim()}
        onClick={handleClick}
        disabled={disabled}
      >
        {label}
      </button>
      <input
        ref={inputRef}
        type='file'
        accept='image/*'
        capture='environment'
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </>
  )
}

export default CameraCaptureButton
